import {
    ADDRESS_CLAMP_TO_EDGE,
    BLEND_NONE,
    BLEND_NORMAL,
    CULLFACE_NONE,
    FILTER_LINEAR,
    PIXELFORMAT_R8_G8_B8_A8,
    StandardMaterial,
    Texture,
    Vec3
} from 'playcanvas';
import type { Application, Entity } from 'playcanvas';
import type { SceneObject, VideoScreenConfig } from './SceneObjectTypes';

type VideoFrameCallbackMetadata = {
    mediaTime?: number;
    presentedFrames?: number;
};

type VideoFrameCallbackVideo = HTMLVideoElement & {
    requestVideoFrameCallback?: (callback: (now: number, metadata: VideoFrameCallbackMetadata) => void) => number;
    cancelVideoFrameCallback?: (handle: number) => void;
};

interface TransformLike {
    transformPoint: (point: Vec3, result: Vec3) => Vec3;
}

interface MeshNodeLike {
    getWorldTransform?: () => TransformLike;
}

interface MeshLike {
    getPositions?: (positions: number[] | ArrayBufferView) => number;
}

interface CameraLike {
    worldToScreen: (worldCoord: Vec3, screenCoord: Vec3) => Vec3;
}

interface ProjectionContext {
    cameraComponent: CameraLike;
    cameraPos: Vec3;
    forward: Vec3;
    cssScaleX: number;
    cssScaleY: number;
    hostWidth: number;
    hostHeight: number;
}

interface MeshInstanceLike {
    material: unknown;
    mesh?: MeshLike;
    node?: MeshNodeLike;
}

interface MeshComponentLike {
    meshInstances?: MeshInstanceLike[];
    model?: {
        meshInstances?: MeshInstanceLike[];
    };
}

interface ScreenRect {
    left: number;
    top: number;
    width: number;
    height: number;
    clipPath: string;
    projectionSource: 'mesh' | 'fallback-transform';
    points: number;
    hostWidth: number;
    hostHeight: number;
}

interface VideoEntry {
    id: string;
    entity: Entity;
    config: Required<VideoScreenConfig>;
    video: HTMLVideoElement;
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D | null;
    texture: Texture;
    posterTexture: Texture | null;
    posterCanvas: HTMLCanvasElement;
    posterCtx: CanvasRenderingContext2D | null;
    mobileVideo: boolean;
    clones: Map<StandardMaterial, StandardMaterial>;
    applied: WeakSet<StandardMaterial>;
    maxTextureWidth: number;
    maxTextureHeight: number;
    uploadMinIntervalMs: number;
    shown: boolean;
    visible: boolean;
    sourceMode: 'video' | 'canvas' | 'domOverlay';
    sourceAttached: boolean;
    lastUploadMs: number;
    lastUploadedVideoTime: number;
    videoFrameRequest: number | null;
    videoUploadFailureLogged: boolean;
    canvasUploadFailureLogged: boolean;
    materialLogged: boolean;
    lastFrameLogMs: number;
    lastVideoProbeAttemptMs: number;
    lastVideoProbeLogMs: number;
    lastVideoStageLogMs: number;
    lastVideoDirectStageLogMs: number;
    videoFrameCallbackSupported: boolean;
    videoFrameCallbackSeen: boolean;
    lastVideoFrameCallbackLogMs: number;
    lastOverlayLogMs: number;
    lastOverlayHiddenLogMs: number;
    uploadedFrameCount: number;
    videoCanvasFallbackLogged: boolean;
    createdAtMs: number;
}

interface VideoCanvasProbe {
    usable: boolean;
    nonBlack: boolean;
    brightPixels: number;
    totalPixels: number;
    sample: string;
}

export interface VideoControlState {
    id: string;
    paused: boolean;
    muted: boolean;
    currentTime: number;
    duration: number;
    canSeek: boolean;
}

const DEFAULT_VIDEO: Required<VideoScreenConfig> = {
    url: '',
    mobileUrl: '',
    posterUrl: '',
    autoplay: false,
    defaultVisible: true,
    loop: true,
    muted: true,
    volume: 1,
    emissiveIntensity: 1.8
};

const VIDEO_DESKTOP_MAX_WIDTH = 1280;
const VIDEO_DESKTOP_MAX_HEIGHT = 720;
const VIDEO_DESKTOP_UPLOAD_FPS = 12;
const VIDEO_MOBILE_MAX_WIDTH = 640;
const VIDEO_MOBILE_MAX_HEIGHT = 360;
const VIDEO_MOBILE_UPLOAD_FPS = 8;
const VIDEO_MOBILE_WARMUP_MS = 200;
const VIDEO_MOBILE_DIRECT_UPLOAD_TIMEOUT_MS = 1000;
const VIDEO_MOBILE_FRAME_CALLBACK_TIMEOUT_MS = 200;
const VIDEO_MOBILE_FIRST_PROBE_INTERVAL_MS = 200;
const VIDEO_MOBILE_ATTACHED_PROBE_INTERVAL_MS = 1000;
const MOBILE_COMPATIBLE_VIDEO_MIME = 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"';
const VIDEO_OVERLAY_MIN_WIDTH = 48;
const VIDEO_OVERLAY_MIN_HEIGHT = 24;
const VIDEO_OVERLAY_MIN_ASPECT = 1.2;
const VIDEO_FALLBACK_PLANES = [
    {
        name: 'xz',
        corners: [
            new Vec3(-0.5, 0, 0.5),
            new Vec3(0.5, 0, 0.5),
            new Vec3(0.5, 0, -0.5),
            new Vec3(-0.5, 0, -0.5)
        ]
    },
    {
        name: 'xy',
        corners: [
            new Vec3(-0.5, 0.5, 0),
            new Vec3(0.5, 0.5, 0),
            new Vec3(0.5, -0.5, 0),
            new Vec3(-0.5, -0.5, 0)
        ]
    },
    {
        name: 'yz',
        corners: [
            new Vec3(0, -0.5, 0.5),
            new Vec3(0, 0.5, 0.5),
            new Vec3(0, 0.5, -0.5),
            new Vec3(0, -0.5, -0.5)
        ]
    }
] as const;
const _videoWorld = new Vec3();
const _videoScreen = new Vec3();
const _videoLocal = new Vec3();
const _videoMeshPositions: number[] = [];

function isMobileVideoRuntime(): boolean {
    return document.getElementById('app-root')?.classList.contains('mobile-runtime') ??
        window.matchMedia('(pointer: coarse)').matches;
}

function selectVideoUrl(config: Required<VideoScreenConfig>, mobileVideo: boolean): string {
    const mobileUrl = config.mobileUrl.trim();
    return mobileVideo && mobileUrl ? mobileUrl : config.url;
}

function isMp4Url(url: string): boolean {
    return /\.mp4(?:[?#]|$)/i.test(url);
}

function warnIfMobileVideoMayBeUnsupported(video: HTMLVideoElement, url: string, mobileVideo: boolean): void {
    if (!mobileVideo) return;
    if (!isMp4Url(url)) {
        console.warn(`[videoScreen] mobile video should be MP4/H.264/AAC: ${url}`);
        return;
    }
    if (video.canPlayType(MOBILE_COMPATIBLE_VIDEO_MIME) === '') {
        console.warn(`[videoScreen] browser did not report MP4/H.264/AAC support: ${url}`);
    }
}

function configureVideoDecodeSurface(video: HTMLVideoElement, mobileVideo: boolean): void {
    video.controls = false;
    video.removeAttribute('controls');
    const controlsList = (video as HTMLVideoElement & { controlsList?: DOMTokenList }).controlsList;
    if (controlsList) controlsList.add('nodownload', 'noplaybackrate', 'noremoteplayback');
    (video as HTMLVideoElement & { disableRemotePlayback?: boolean }).disableRemotePlayback = true;
    video.style.position = mobileVideo ? 'absolute' : 'fixed';
    video.style.left = '0';
    video.style.top = '0';
    video.style.display = 'block';
    video.style.width = mobileVideo ? '2px' : '1px';
    video.style.height = mobileVideo ? '2px' : '1px';
    video.style.pointerEvents = 'none';
    video.style.objectFit = 'fill';
    video.style.zIndex = '0';
    video.style.clipPath = 'none';
    video.style.removeProperty('contain');
    if (mobileVideo) {
        video.style.removeProperty('opacity');
        video.style.removeProperty('transform');
        video.style.removeProperty('transform-origin');
    } else {
        video.style.opacity = '0';
        video.style.transformOrigin = '0 0';
        video.style.transform = 'translate(-9999px, -9999px)';
    }
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
}

function getVideoMeshInstances(e: Entity): MeshInstanceLike[] {
    const renderMeshes = (e.render as unknown as MeshComponentLike | undefined)?.meshInstances ?? [];
    const modelComponent = e.model as unknown as MeshComponentLike | undefined;
    const modelMeshes = modelComponent?.meshInstances ?? modelComponent?.model?.meshInstances ?? [];
    return renderMeshes.length || modelMeshes.length ? [...renderMeshes, ...modelMeshes] : [];
}

export class VideoScreenSystem {
    private _app: Application;
    private _entries = new Map<string, VideoEntry>();
    private _lastProjectionFailure: string | null = null;

    constructor(app: Application) {
        this._app = app;
    }

    attach(obj: SceneObject, entity: Entity): void {
        if (!obj.video) return;
        this.detach(obj.id);
        const config = { ...DEFAULT_VIDEO, ...obj.video };
        const mobileVideo = isMobileVideoRuntime();
        const url = selectVideoUrl(config, mobileVideo);
        if (!url.trim()) return;

        const video = document.createElement('video');
        warnIfMobileVideoMayBeUnsupported(video, url, mobileVideo);
        video.crossOrigin = 'anonymous';
        video.src = url;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('x5-playsinline', '');
        video.setAttribute('x5-video-player-type', 'h5');
        video.setAttribute('x5-video-player-fullscreen', 'false');
        video.setAttribute('x-webkit-airplay', 'deny');
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.preload = 'auto';
        video.loop = config.loop;
        video.muted = mobileVideo ? true : config.muted;
        video.volume = Math.max(0, Math.min(1, config.volume));
        (video as HTMLVideoElement & { disablePictureInPicture?: boolean }).disablePictureInPicture = true;
        configureVideoDecodeSurface(video, mobileVideo);
        if (mobileVideo) {
            const appRoot = document.getElementById('app-root');
            const appCanvas = appRoot?.querySelector('canvas');
            if (appRoot && appCanvas) {
                appRoot.insertBefore(video, appCanvas);
            } else {
                (document.getElementById('ui-layer') ?? document.body).appendChild(video);
            }
        } else {
            document.body.appendChild(video);
        }

        const canvas = document.createElement('canvas');
        canvas.width = 4;
        canvas.height = 4;
        const ctx = canvas.getContext('2d', { alpha: false });
        if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
        }

        const posterCanvas = document.createElement('canvas');
        posterCanvas.width = mobileVideo ? VIDEO_MOBILE_MAX_WIDTH : VIDEO_DESKTOP_MAX_WIDTH;
        posterCanvas.height = mobileVideo ? VIDEO_MOBILE_MAX_HEIGHT : VIDEO_DESKTOP_MAX_HEIGHT;
        const posterCtx = posterCanvas.getContext('2d', { alpha: false });
        if (posterCtx) {
            posterCtx.imageSmoothingEnabled = true;
            posterCtx.imageSmoothingQuality = 'high';
        }

        const texture = new Texture(this._app.graphicsDevice, {
            name: `video-screen:${obj.id}`,
            format: PIXELFORMAT_R8_G8_B8_A8,
            width: 4,
            height: 4,
            mipmaps: false,
            minFilter: FILTER_LINEAR,
            magFilter: FILTER_LINEAR,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });

        const entry: VideoEntry = {
            id: obj.id,
            entity,
            config,
            video,
            canvas,
            ctx,
            texture,
            posterTexture: null,
            posterCanvas,
            posterCtx,
            mobileVideo,
            clones: new Map(),
            applied: new WeakSet(),
            maxTextureWidth: mobileVideo ? VIDEO_MOBILE_MAX_WIDTH : VIDEO_DESKTOP_MAX_WIDTH,
            maxTextureHeight: mobileVideo ? VIDEO_MOBILE_MAX_HEIGHT : VIDEO_DESKTOP_MAX_HEIGHT,
            uploadMinIntervalMs: mobileVideo ? 1000 / VIDEO_MOBILE_UPLOAD_FPS : 1000 / VIDEO_DESKTOP_UPLOAD_FPS,
            shown: config.defaultVisible !== false,
            visible: config.defaultVisible !== false,
            sourceMode: 'video',
            sourceAttached: false,
            lastUploadMs: Number.NEGATIVE_INFINITY,
            lastUploadedVideoTime: Number.NEGATIVE_INFINITY,
            videoFrameRequest: null,
            videoUploadFailureLogged: false,
            canvasUploadFailureLogged: false,
            materialLogged: false,
            lastFrameLogMs: Number.NEGATIVE_INFINITY,
            lastVideoProbeAttemptMs: Number.NEGATIVE_INFINITY,
            lastVideoProbeLogMs: Number.NEGATIVE_INFINITY,
            lastVideoStageLogMs: Number.NEGATIVE_INFINITY,
            lastVideoDirectStageLogMs: Number.NEGATIVE_INFINITY,
            videoFrameCallbackSupported: typeof (video as VideoFrameCallbackVideo).requestVideoFrameCallback === 'function',
            videoFrameCallbackSeen: false,
            lastVideoFrameCallbackLogMs: Number.NEGATIVE_INFINITY,
            lastOverlayLogMs: Number.NEGATIVE_INFINITY,
            lastOverlayHiddenLogMs: Number.NEGATIVE_INFINITY,
            uploadedFrameCount: 0,
            videoCanvasFallbackLogged: false,
            createdAtMs: performance.now()
        };
        this._entries.set(obj.id, entry);
        const posterUrl = config.posterUrl.trim();
        if (posterUrl) this._loadPosterTexture(entry, posterUrl);
        this._logVideoState(entry, 'attach');
        video.addEventListener('loadedmetadata', () => this._logVideoState(entry, 'loadedmetadata'), { once: true });
        video.addEventListener('loadeddata', () => {
            this._logVideoState(entry, 'loadeddata');
            if (entry.shown && entry.sourceMode !== 'domOverlay') this._uploadFrame(entry, true);
        }, { once: true });
        video.addEventListener('canplay', () => this._logVideoState(entry, 'canplay'), { once: true });
        video.addEventListener('playing', () => this._logVideoState(entry, 'playing'));
        video.addEventListener('waiting', () => this._logVideoState(entry, 'waiting'));
        video.addEventListener('stalled', () => this._logVideoState(entry, 'stalled'));
        video.addEventListener('error', () => this._logVideoState(entry, 'error'));
        this._startVideoFramePump(entry);

    }

    detach(id: string): void {
        const entry = this._entries.get(id);
        if (!entry) return;
        const frameVideo = entry.video as VideoFrameCallbackVideo;
        if (entry.videoFrameRequest !== null && frameVideo.cancelVideoFrameCallback) {
            frameVideo.cancelVideoFrameCallback(entry.videoFrameRequest);
        }
        entry.video.pause();
        entry.video.removeAttribute('src');
        entry.video.load();
        entry.video.remove();
        entry.texture.destroy();
        entry.posterTexture?.destroy();
        this._entries.delete(id);
    }

    getState(id: string): VideoControlState | null {
        const entry = this._entries.get(id);
        if (!entry) return null;
        const video = entry.video;
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        return {
            id,
            paused: video.paused,
            muted: video.muted,
            currentTime: Number.isFinite(video.currentTime) ? video.currentTime : 0,
            duration,
            canSeek: duration > 0
        };
    }

    togglePlayback(id: string): void {
        const entry = this._entries.get(id);
        if (!entry) return;
        if (entry.video.paused) {
            this._play(entry);
        } else {
            entry.video.pause();
        }
    }

    show(id: string): void {
        const entry = this._entries.get(id);
        if (!entry) return;
        entry.shown = true;
        entry.visible = true;
    }

    hide(id: string): void {
        const entry = this._entries.get(id);
        if (!entry) return;
        entry.video.pause();
        entry.shown = false;
        entry.visible = false;
    }

    /** Pause every currently-playing video, returning the list of IDs that were
     *  playing (so the caller can resume them later via resume()). */
    pauseAllPlaying(): string[] {
        const ids: string[] = [];
        for (const [id, entry] of this._entries) {
            if (!entry.video.paused) {
                entry.video.pause();
                ids.push(id);
            }
        }
        return ids;
    }

    /** Resume videos whose IDs were returned by pauseAllPlaying(). */
    resume(ids: string[]): void {
        for (const id of ids) {
            const entry = this._entries.get(id);
            if (entry && entry.video.paused) {
                this._play(entry);
            }
        }
    }

    toggleMuted(id: string): void {
        const entry = this._entries.get(id);
        if (!entry) return;
        entry.video.muted = !entry.video.muted;
    }

    seek(id: string, time: number): void {
        const entry = this._entries.get(id);
        if (!entry) return;
        const duration = Number.isFinite(entry.video.duration) ? entry.video.duration : 0;
        if (duration <= 0) return;
        entry.video.currentTime = Math.max(0, Math.min(duration, time));
    }

    setVisible(id: string, visible: boolean): void {
        const entry = this._entries.get(id);
        if (!entry) return;
        entry.visible = visible;
    }

    update(): void {
        for (const entry of this._entries.values()) {
            this._applyMaterial(entry);
            if (!entry.shown) {
                this._hideDomOverlay(entry, 'not-shown');
                continue;
            }
            if (entry.sourceMode === 'domOverlay') {
                this._updateDomOverlay(entry);
                continue;
            }
            if (entry.video.readyState >= 2 && !entry.sourceAttached) {
                this._uploadFrame(entry, true);
            }
            if (!entry.visible) continue;
            if (entry.video.readyState < 2) continue;
            this._uploadFrame(entry, false);
        }
    }

    destroy(): void {
        for (const id of [...this._entries.keys()]) this.detach(id);
    }

    private _loadPosterTexture(entry: VideoEntry, url: string): void {
        if (!entry.posterCtx) return;
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            if (!this._entries.has(entry.id)) return;
            this._drawPosterImageContained(entry, image);
            if (!entry.posterTexture) {
                entry.posterTexture = new Texture(this._app.graphicsDevice, {
                    name: `video-screen-poster:${entry.id}`,
                    format: PIXELFORMAT_R8_G8_B8_A8,
                    width: entry.posterCanvas.width,
                    height: entry.posterCanvas.height,
                    mipmaps: false,
                    minFilter: FILTER_LINEAR,
                    magFilter: FILTER_LINEAR,
                    addressU: ADDRESS_CLAMP_TO_EDGE,
                    addressV: ADDRESS_CLAMP_TO_EDGE
                });
            }
            entry.posterTexture.setSource(entry.posterCanvas);
            entry.posterTexture.upload();
            entry.materialLogged = false;
            console.info(
                `[videoScreen] poster loaded: id=${entry.id} url=${url}` +
                ` image=${image.naturalWidth}x${image.naturalHeight}` +
                ` canvas=${entry.posterCanvas.width}x${entry.posterCanvas.height}`
            );
        };
        image.onerror = (err) => {
            if (!this._entries.has(entry.id)) return;
            console.warn(`[videoScreen] poster load failed: id=${entry.id} url=${url}`, err);
        };
        image.src = url;
    }

    private _drawPosterImageContained(entry: VideoEntry, image: HTMLImageElement): void {
        if (!entry.posterCtx) return;
        const canvasWidth = entry.posterCanvas.width;
        const canvasHeight = entry.posterCanvas.height;
        entry.posterCtx.fillStyle = '#000';
        entry.posterCtx.fillRect(0, 0, canvasWidth, canvasHeight);
        const scale = Math.min(
            canvasWidth / Math.max(1, image.naturalWidth),
            canvasHeight / Math.max(1, image.naturalHeight)
        );
        const drawWidth = image.naturalWidth * scale;
        const drawHeight = image.naturalHeight * scale;
        const drawX = (canvasWidth - drawWidth) / 2;
        const drawY = (canvasHeight - drawHeight) / 2;
        entry.posterCtx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    }

    private _applyMaterial(entry: VideoEntry): void {
        let meshCount = 0;
        let appliedCount = 0;
        entry.entity.forEach((node) => {
            const e = node as Entity;
            const meshInstances = getVideoMeshInstances(e);
            if (!meshInstances) return;
            meshCount += meshInstances.length;
            for (const mi of meshInstances) {
                const mat = mi.material;
                if (!(mat instanceof StandardMaterial)) continue;
                if (entry.applied.has(mat)) {
                    this._configureVideoMaterial(mat, entry);
                    appliedCount++;
                    continue;
                }
                let copy = entry.clones.get(mat);
                if (!copy) {
                    copy = mat.clone() as StandardMaterial;
                    entry.clones.set(mat, copy);
                    entry.applied.add(copy);
                }
                this._configureVideoMaterial(copy, entry);
                mi.material = copy;
                appliedCount++;
            }
        });
        if (!entry.materialLogged && meshCount > 0) {
            entry.materialLogged = true;
            this._logMaterialBinding(entry, meshCount, appliedCount);
        }
    }

    private _configureVideoMaterial(mat: StandardMaterial, entry: VideoEntry): void {
        const domOverlay = entry.sourceMode === 'domOverlay';
        const defaultHidden = !entry.shown;
        const materialTexture = entry.sourceAttached ? entry.texture : (entry.posterTexture ?? entry.texture);
        mat.diffuse.set(1, 1, 1);
        mat.emissive.set(1, 1, 1);
        mat.emissiveIntensity = entry.config.emissiveIntensity;
        mat.diffuseMap = materialTexture;
        mat.emissiveMap = materialTexture;
        mat.blendType = BLEND_NONE;
        mat.opacity = defaultHidden || domOverlay ? 0 : 1;
        mat.blendType = defaultHidden || domOverlay ? BLEND_NORMAL : BLEND_NONE;
        mat.useLighting = false;
        mat.useSkybox = false;
        mat.useFog = false;
        mat.cull = CULLFACE_NONE;
        mat.depthTest = !defaultHidden && entry.sourceMode !== 'domOverlay';
        mat.depthWrite = !defaultHidden && entry.sourceMode !== 'domOverlay';
        mat.update();
    }

    private _play(entry: VideoEntry): void {
        void entry.video.play().then(() => {
            this._logVideoState(entry, 'play resolved');
        }).catch((err) => {
            this._logVideoState(entry, 'play rejected');
            if (!entry.video.muted) {
                entry.video.muted = true;
                void entry.video.play().then(() => {
                    this._logVideoState(entry, 'muted play resolved');
                }).catch((retryErr) => {
                    this._logVideoState(entry, 'muted play rejected');
                    console.warn(`[videoScreen] playback failed: ${entry.video.currentSrc || entry.config.url}`, retryErr);
                });
                return;
            }
            console.warn(`[videoScreen] playback failed: ${entry.video.currentSrc || entry.config.url}`, err);
        });
    }

    private _startVideoFramePump(entry: VideoEntry): void {
        const video = entry.video as VideoFrameCallbackVideo;

        // Primary: requestVideoFrameCallback for frame-aligned uploads.
        if (video.requestVideoFrameCallback) {
            const pump = () => {
                entry.videoFrameRequest = video.requestVideoFrameCallback((_now, metadata) => {
                    if (!this._entries.has(entry.id)) return;
                    entry.videoFrameCallbackSeen = true;
                    this._logVideoFrameCallback(entry, metadata);
                    if (entry.video.readyState >= 2 && (entry.visible || !entry.sourceAttached)) {
                        this._uploadFrame(entry, !entry.sourceAttached);
                    }
                    pump();
                });
            };
            pump();
        }

        // Fallback: timer-based polling for mobile browsers where rVFC may
        // never fire. The viewport decode surface addresses the root cause,
        // and this fallback guards against remaining callback timing gaps.
        if (entry.mobileVideo) {
            let lastTime = Number.isFinite(entry.video.currentTime) ? entry.video.currentTime : 0;
            const pollMs = 200;
            const pollTimer = setInterval(() => {
                if (!this._entries.has(entry.id)) {
                    clearInterval(pollTimer);
                    return;
                }
                if (entry.videoFrameCallbackSeen) {
                    clearInterval(pollTimer);
                    return;
                }
                const t = entry.video.currentTime;
                if (t !== lastTime && Number.isFinite(t)) {
                    lastTime = t;
                    entry.videoFrameCallbackSeen = true; // unblock upload delay gate
                    if (entry.video.readyState >= 2 && !entry.sourceAttached) {
                        this._uploadFrame(entry, true);
                    }
                }
                if (performance.now() - entry.createdAtMs > 10000) {
                    clearInterval(pollTimer);
                }
            }, pollMs);
        }
    }

    private _uploadFrame(entry: VideoEntry, force = false): void {
        if (entry.sourceMode === 'domOverlay') return;
        const now = performance.now();
        if (!force && !this._shouldUploadFrame(entry, now)) return;

        let uploaded = false;
        if (entry.sourceMode === 'video') {
            uploaded = this._uploadVideoFrame(entry);
        } else {
            uploaded = this._uploadCanvasFrame(entry);
        }
        if (!uploaded) return;

        entry.lastUploadMs = now;
        entry.lastUploadedVideoTime = entry.video.currentTime;
        entry.uploadedFrameCount++;
    }

    private _uploadVideoFrame(entry: VideoEntry): boolean {
        try {
            const now = performance.now();
            const probe = entry.mobileVideo ? this._probeMobileVideoCanvas(entry, now) : null;
            if (probe) {
                this._logVideoCanvasProbe(entry, probe);
            }
            const delayReason = this._shouldDelayMobileVideoUpload(entry, probe, now);
            if (delayReason) {
                this._logVideoUploadStage(entry, 'delayed', delayReason);
                return false;
            }
            if (probe) {
                if (probe.usable && probe.nonBlack) {
                    if (!entry.videoCanvasFallbackLogged) {
                        entry.videoCanvasFallbackLogged = true;
                        console.info(
                            `[videoScreen] switching to canvas texture: id=${entry.id}` +
                            ` reason=mobile-direct-video-probe sample=${probe.sample}`
                        );
                    }
                    entry.sourceMode = 'canvas';
                    entry.sourceAttached = false;
                    return this._uploadCanvasFrame(entry);
                }
            }
            const firstAttach = !entry.sourceAttached;
            if (!entry.sourceAttached) {
                const setSourceStartedAt = performance.now();
                entry.texture.setSource(entry.video);
                this._logVideoUploadStage(entry, 'setSource(video)', `durationMs=${Math.round(performance.now() - setSourceStartedAt)}`);
                entry.sourceAttached = true;
            }
            const uploadStartedAt = performance.now();
            entry.texture.upload();
            const gpuError = this._graphicsDeviceError();
            this._logVideoUploadStage(entry, 'upload(video)', `durationMs=${Math.round(performance.now() - uploadStartedAt)} gpuError=${gpuError}`);
            if (this._isGpuUploadError(gpuError)) {
                throw new Error(`GPU error after video texture upload: ${gpuError}`);
            }
            if (firstAttach) {
                this._logVideoState(entry, 'texture upload(video)');
            }
            this._logFrameUpload(entry, 'video', false, gpuError);
            return true;
        } catch (err) {
            if (!entry.videoUploadFailureLogged) {
                entry.videoUploadFailureLogged = true;
                console.warn(`[videoScreen] video texture upload failed: ${entry.video.currentSrc || entry.config.url}`, err);
                this._logVideoState(entry, 'texture upload(video) failed');
            }
            entry.sourceMode = 'canvas';
            entry.sourceAttached = false;
            return this._uploadCanvasFrame(entry);
        }
    }

    private _shouldDelayMobileVideoUpload(entry: VideoEntry, probe: VideoCanvasProbe | null, now: number): string | null {
        if (!entry.mobileVideo) return null;
        const elapsedMs = now - entry.createdAtMs;
        if (entry.video.videoWidth <= 0 || entry.video.videoHeight <= 0) return 'waiting-video-dimensions';
        if (entry.video.videoWidth < 320 || entry.video.videoHeight < 180) return `waiting-real-size ${entry.video.videoWidth}x${entry.video.videoHeight}`;
        if (entry.videoFrameCallbackSupported && !entry.videoFrameCallbackSeen && elapsedMs < VIDEO_MOBILE_FRAME_CALLBACK_TIMEOUT_MS) {
            return `waiting-video-frame-callback elapsedMs=${Math.round(elapsedMs)}`;
        }
        if (elapsedMs < VIDEO_MOBILE_WARMUP_MS) return `warmup elapsedMs=${Math.round(elapsedMs)}`;
        if (probe?.usable && probe.nonBlack) return null;
        if (elapsedMs < VIDEO_MOBILE_DIRECT_UPLOAD_TIMEOUT_MS) {
            return `waiting-nonblack-probe elapsedMs=${Math.round(elapsedMs)} sample=${probe?.sample ?? 'n/a'}`;
        }
        return null;
    }

    private _probeMobileVideoCanvas(entry: VideoEntry, now: number): VideoCanvasProbe | null {
        const intervalMs = entry.sourceAttached ? VIDEO_MOBILE_ATTACHED_PROBE_INTERVAL_MS : VIDEO_MOBILE_FIRST_PROBE_INTERVAL_MS;
        if (now - entry.lastVideoProbeAttemptMs < intervalMs) return null;
        entry.lastVideoProbeAttemptMs = now;
        return this._probeVideoCanvas(entry);
    }

    private _uploadCanvasFrame(entry: VideoEntry): boolean {
        if (!entry.ctx) return false;
        const resized = this._resizeFrameCanvas(entry);
        try {
            entry.ctx.drawImage(entry.video, 0, 0, entry.canvas.width, entry.canvas.height);
        } catch (err) {
            if (!entry.canvasUploadFailureLogged) {
                entry.canvasUploadFailureLogged = true;
                console.warn(`[videoScreen] canvas drawImage failed: ${entry.video.currentSrc || entry.config.url}`, err);
                this._logVideoState(entry, 'canvas draw failed');
            }
            return false;
        }

        const firstAttach = !entry.sourceAttached;
        let gpuError = 'n/a';
        try {
            if (!entry.sourceAttached || resized) {
                entry.texture.setSource(entry.canvas);
                entry.sourceAttached = true;
            }
            entry.texture.upload();
            gpuError = this._graphicsDeviceError();
            if (this._isGpuUploadError(gpuError)) {
                throw new Error(`GPU error after canvas texture upload: ${gpuError}`);
            }
        } catch (err) {
            entry.sourceAttached = false;
            if (!entry.canvasUploadFailureLogged) {
                entry.canvasUploadFailureLogged = true;
                console.warn(`[videoScreen] canvas texture upload failed: ${entry.video.currentSrc || entry.config.url}`, err);
                this._logVideoState(entry, 'texture upload(canvas) failed');
            }
            return false;
        }
        if (firstAttach) {
            this._logVideoState(entry, 'texture upload(canvas)');
        }
        this._logFrameUpload(entry, 'canvas', resized, gpuError);
        return true;
    }

    private _probeVideoCanvas(entry: VideoEntry): VideoCanvasProbe | null {
        if (!entry.ctx) return null;
        if (entry.video.videoWidth <= 0 || entry.video.videoHeight <= 0) return null;
        const width = 32;
        const height = Math.max(2, Math.round(width * entry.video.videoHeight / Math.max(1, entry.video.videoWidth)));
        if (entry.canvas.width !== width || entry.canvas.height !== height) {
            entry.canvas.width = width;
            entry.canvas.height = height;
            entry.ctx.imageSmoothingEnabled = true;
            entry.ctx.imageSmoothingQuality = 'high';
        }
        try {
            entry.ctx.drawImage(entry.video, 0, 0, width, height);
            const points = [
                [0.2, 0.2], [0.5, 0.2], [0.8, 0.2],
                [0.2, 0.5], [0.5, 0.5], [0.8, 0.5],
                [0.2, 0.8], [0.5, 0.8], [0.8, 0.8]
            ];
            let brightPixels = 0;
            const samples: string[] = [];
            for (const [px, py] of points) {
                const x = Math.min(width - 1, Math.max(0, Math.floor(width * px)));
                const y = Math.min(height - 1, Math.max(0, Math.floor(height * py)));
                const data = entry.ctx.getImageData(x, y, 1, 1).data;
                const maxChannel = Math.max(data[0], data[1], data[2]);
                if (maxChannel > 24) brightPixels++;
                if (samples.length < 4) samples.push(`${data[0]},${data[1]},${data[2]},${data[3]}`);
            }
            return {
                usable: true,
                nonBlack: brightPixels > 0,
                brightPixels,
                totalPixels: points.length,
                sample: `${brightPixels}/${points.length}:${samples.join('|')}`
            };
        } catch (err) {
            return {
                usable: false,
                nonBlack: false,
                brightPixels: 0,
                totalPixels: 0,
                sample: `error:${err instanceof Error ? err.name : String(err)}`
            };
        }
    }

    private _updateDomOverlay(entry: VideoEntry): void {
        if (!entry.mobileVideo) return;
        if (!entry.visible) {
            this._hideDomOverlay(entry, 'not-visible');
            return;
        }
        if (entry.video.readyState < 2) {
            this._hideDomOverlay(entry, `video-readyState=${entry.video.readyState}`);
            return;
        }

        const rect = this._projectEntityToScreenRect(entry);
        if (!rect) {
            this._hideDomOverlay(entry, this._lastProjectionFailure ?? 'projection-miss');
            return;
        }

        entry.video.style.opacity = '1';
        entry.video.style.width = `${Math.max(1, Math.round(rect.width))}px`;
        entry.video.style.height = `${Math.max(1, Math.round(rect.height))}px`;
        entry.video.style.transform = `translate(${Math.round(rect.left)}px, ${Math.round(rect.top)}px)`;
        entry.video.style.clipPath = rect.clipPath;
        entry.video.style.zIndex = '1'; // above canvas (in #app-root before canvas in DOM)
        this._logDomOverlay(entry, rect);
    }

    private _hideDomOverlay(entry: VideoEntry, reason = 'hidden'): void {
        entry.video.style.opacity = '0';
        entry.video.style.transform = 'translate(-9999px, -9999px)';
        entry.video.style.clipPath = 'none';
        entry.video.style.zIndex = ''; // reset — DOM order hides it again
        this._logOverlayHidden(entry, reason);
    }

    private _projectEntityToScreenRect(entry: VideoEntry): ScreenRect | null {
        this._lastProjectionFailure = null;
        const context = this._makeProjectionContext(entry);
        if (!context) return null;

        const meshRect = this._projectMeshToScreenRect(entry, context);
        if (meshRect) return meshRect;
        const meshFailure = this._lastProjectionFailure;

        const fallbackRect = this._projectFallbackTransformRect(entry, context);
        if (fallbackRect) return fallbackRect;
        if (meshFailure) {
            this._lastProjectionFailure = `${meshFailure}; ${this._lastProjectionFailure ?? 'fallback failed'}`;
        }
        return null;
    }

    private _makeProjectionContext(entry: VideoEntry): ProjectionContext | null {
        const camera = this._app.root.findByName('Camera') as Entity | null;
        if (!camera?.camera) {
            this._lastProjectionFailure = 'projection-miss:no-camera';
            return null;
        }
        const cameraComponent = camera.camera;

        const canvas = this._app.graphicsDevice.canvas as HTMLCanvasElement;
        const cssScaleX = canvas.clientWidth / Math.max(1, canvas.width);
        const cssScaleY = canvas.clientHeight / Math.max(1, canvas.height);
        const host = entry.video.parentElement ?? document.getElementById('ui-layer');
        const hostWidth = host?.clientWidth || canvas.clientWidth || window.innerWidth;
        const hostHeight = host?.clientHeight || canvas.clientHeight || window.innerHeight;
        return {
            cameraComponent,
            cameraPos: camera.getPosition(),
            forward: camera.forward,
            cssScaleX,
            cssScaleY,
            hostWidth,
            hostHeight
        };
    }

    private _projectMeshToScreenRect(entry: VideoEntry, context: ProjectionContext): ScreenRect | null {
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        let pointCount = 0;
        let meshCount = 0;
        let vertexCountTotal = 0;
        let behindCount = 0;
        let invalidCount = 0;

        entry.entity.forEach((node) => {
            const e = node as Entity;
            const meshInstances = getVideoMeshInstances(e);
            for (const mi of meshInstances) {
                const mesh = mi.mesh;
                if (!mesh?.getPositions) continue;
                meshCount++;
                _videoMeshPositions.length = 0;
                const vertexCount = mesh.getPositions(_videoMeshPositions);
                const usableVertexCount = Math.min(vertexCount, Math.floor(_videoMeshPositions.length / 3));
                vertexCountTotal += usableVertexCount;
                if (usableVertexCount <= 0) continue;
                const transform = mi.node?.getWorldTransform?.() ?? e.getWorldTransform();
                for (let i = 0; i < usableVertexCount; i++) {
                    const offset = i * 3;
                    const localX = _videoMeshPositions[offset];
                    const localY = _videoMeshPositions[offset + 1];
                    const localZ = _videoMeshPositions[offset + 2];
                    if (!Number.isFinite(localX) || !Number.isFinite(localY) || !Number.isFinite(localZ)) {
                        invalidCount++;
                        continue;
                    }
                    _videoLocal.set(localX, localY, localZ);
                    transform.transformPoint(_videoLocal, _videoWorld);
                    if (this._isBehindCamera(_videoWorld, context)) behindCount++;
                    context.cameraComponent.worldToScreen(_videoWorld, _videoScreen);
                    const screenX = _videoScreen.x * context.cssScaleX;
                    const screenY = _videoScreen.y * context.cssScaleY;
                    if (!Number.isFinite(screenX) || !Number.isFinite(screenY)) {
                        invalidCount++;
                        continue;
                    }
                    minX = Math.min(minX, screenX);
                    minY = Math.min(minY, screenY);
                    maxX = Math.max(maxX, screenX);
                    maxY = Math.max(maxY, screenY);
                    pointCount++;
                }
            }
        });

        if (meshCount <= 0 || vertexCountTotal <= 0) {
            this._lastProjectionFailure = `projection-miss:no-mesh-positions meshes=${meshCount} vertices=${vertexCountTotal}`;
            return null;
        }
        const rect = this._makeScreenRectFromBounds(
            context,
            minX,
            minY,
            maxX,
            maxY,
            pointCount,
            'mesh',
            `mesh meshes=${meshCount} vertices=${vertexCountTotal} behind=${behindCount} invalid=${invalidCount}`
        );
        return rect ? this._fitLandscapeVideoRect(rect, entry) : null;
    }

    private _projectFallbackTransformRect(entry: VideoEntry, context: ProjectionContext): ScreenRect | null {
        let best: ScreenRect | null = null;
        let bestArea = 0;
        let lastFailure = 'projection-miss:fallback-transform unavailable';
        for (const plane of VIDEO_FALLBACK_PLANES) {
            const rect = this._projectLocalCornersToScreenRect(
                entry,
                context,
                plane.corners,
                `fallback-transform:${plane.name}`
            );
            if (!rect) {
                lastFailure = this._lastProjectionFailure ?? lastFailure;
                continue;
            }
            const area = rect.width * rect.height;
            if (area > bestArea) {
                best = rect;
                bestArea = area;
            }
        }
        if (!best) this._lastProjectionFailure = lastFailure;
        return best;
    }

    private _projectLocalCornersToScreenRect(
        entry: VideoEntry,
        context: ProjectionContext,
        corners: readonly Vec3[],
        label: string
    ): ScreenRect | null {
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        let pointCount = 0;
        let behindCount = 0;
        let invalidCount = 0;
        const transform = entry.entity.getWorldTransform();
        for (const corner of corners) {
            transform.transformPoint(corner, _videoWorld);
            if (this._isBehindCamera(_videoWorld, context)) behindCount++;
            context.cameraComponent.worldToScreen(_videoWorld, _videoScreen);
            const x = _videoScreen.x * context.cssScaleX;
            const y = _videoScreen.y * context.cssScaleY;
            if (!Number.isFinite(x) || !Number.isFinite(y)) {
                invalidCount++;
                continue;
            }
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            pointCount++;
        }
        const rect = this._makeScreenRectFromBounds(
            context,
            minX,
            minY,
            maxX,
            maxY,
            pointCount,
            'fallback-transform',
            `${label} behind=${behindCount} invalid=${invalidCount}`
        );
        return rect ? this._fitLandscapeVideoRect(rect, entry) : null;
    }

    private _makeScreenRectFromBounds(
        context: ProjectionContext,
        minX: number,
        minY: number,
        maxX: number,
        maxY: number,
        pointCount: number,
        projectionSource: ScreenRect['projectionSource'],
        label: string
    ): ScreenRect | null {
        if (pointCount <= 0) {
            this._lastProjectionFailure = `projection-miss:${label} points=0`;
            return null;
        }
        if (maxX < 0 || maxY < 0 || minX > context.hostWidth || minY > context.hostHeight) {
            this._lastProjectionFailure =
                `projection-miss:${label} offscreen rect=${Math.round(minX)},${Math.round(minY)},${Math.round(maxX - minX)}x${Math.round(maxY - minY)}` +
                ` host=${Math.round(context.hostWidth)}x${Math.round(context.hostHeight)}`;
            return null;
        }
        const left = Math.max(0, minX);
        const top = Math.max(0, minY);
        const right = Math.min(context.hostWidth, maxX);
        const bottom = Math.min(context.hostHeight, maxY);
        const width = right - left;
        const height = bottom - top;
        if (width < VIDEO_OVERLAY_MIN_WIDTH || height < VIDEO_OVERLAY_MIN_HEIGHT) {
            this._lastProjectionFailure =
                `projection-miss:${label} too-small rect=${Math.round(left)},${Math.round(top)},${Math.round(width)}x${Math.round(height)}`;
            return null;
        }
        return {
            left,
            top,
            width,
            height,
            clipPath: 'inset(0)',
            projectionSource,
            points: pointCount,
            hostWidth: context.hostWidth,
            hostHeight: context.hostHeight
        };
    }

    private _isBehindCamera(point: Vec3, context: ProjectionContext): boolean {
        const dx = point.x - context.cameraPos.x;
        const dy = point.y - context.cameraPos.y;
        const dz = point.z - context.cameraPos.z;
        return dx * context.forward.x + dy * context.forward.y + dz * context.forward.z < 0;
    }

    private _fitLandscapeVideoRect(rect: ScreenRect, entry: VideoEntry): ScreenRect | null {
        const targetAspect = this._videoAspect(entry);
        const minAspect = Math.max(targetAspect, VIDEO_OVERLAY_MIN_ASPECT);
        let width = rect.width;
        let height = rect.height;
        if (width / Math.max(1, height) < minAspect) {
            height = width / minAspect;
        } else {
            width = height * minAspect;
        }

        let left = rect.left + (rect.width - width) / 2;
        let top = rect.top + (rect.height - height) / 2;
        if (left < 0) left = 0;
        if (top < 0) top = 0;
        if (left + width > rect.hostWidth) left = Math.max(0, rect.hostWidth - width);
        if (top + height > rect.hostHeight) top = Math.max(0, rect.hostHeight - height);
        if (width < VIDEO_OVERLAY_MIN_WIDTH || height < VIDEO_OVERLAY_MIN_HEIGHT) return null;
        return { ...rect, left, top, width, height, clipPath: 'inset(0)' };
    }

    private _videoAspect(entry: VideoEntry): number {
        const width = entry.video.videoWidth || 16;
        const height = entry.video.videoHeight || 9;
        const aspect = width / Math.max(1, height);
        return Number.isFinite(aspect) && aspect > 0 ? aspect : 16 / 9;
    }

    private _logDomOverlay(entry: VideoEntry, rect: ScreenRect): void {
        const now = performance.now();
        if (now - entry.lastOverlayLogMs < 5000) return;
        entry.lastOverlayLogMs = now;
        const video = entry.video;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime.toFixed(2) : 'n/a';
        console.info(
            `[videoScreen] dom overlay: id=${entry.id} visible=true` +
            ` rect=${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.width)}x${Math.round(rect.height)}` +
            ` projectionSource=${rect.projectionSource} points=${rect.points}` +
            ` host=${Math.round(rect.hostWidth)}x${Math.round(rect.hostHeight)}` +
            ` aspect=${(rect.width / Math.max(1, rect.height)).toFixed(2)}` +
            ` projectionFailure=${this._lastProjectionFailure ?? 'none'}` +
            ` video=${video.videoWidth}x${video.videoHeight} readyState=${video.readyState}` +
            ` currentTime=${currentTime}`
        );
    }

    private _logOverlayHidden(entry: VideoEntry, reason: string): void {
        const now = performance.now();
        if (now - entry.lastOverlayHiddenLogMs < 5000) return;
        entry.lastOverlayHiddenLogMs = now;
        const video = entry.video;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime.toFixed(2) : 'n/a';
        console.info(
            `[videoScreen] overlay hidden: id=${entry.id} reason=${reason}` +
            ` sourceMode=${entry.sourceMode} video=${video.videoWidth}x${video.videoHeight}` +
            ` readyState=${video.readyState} currentTime=${currentTime}`
        );
    }

    private _logMaterialBinding(entry: VideoEntry, meshCount: number, appliedCount: number): void {
        console.info(
            `[videoScreen] material bound: id=${entry.id} meshes=${meshCount} applied=${appliedCount}` +
            ` sourceMode=${entry.sourceMode} mobile=${entry.mobileVideo}` +
            ` depthTest=${entry.shown && entry.sourceMode !== 'domOverlay'} depthWrite=${entry.shown && entry.sourceMode !== 'domOverlay'} cull=${CULLFACE_NONE}` +
            ` texture=${entry.texture.width}x${entry.texture.height}`
        );
    }

    private _logFrameUpload(entry: VideoEntry, mode: string, resized: boolean, gpuError = this._graphicsDeviceError()): void {
        const now = performance.now();
        if (entry.uploadedFrameCount > 0 && !resized && now - entry.lastFrameLogMs < 5000) return;
        entry.lastFrameLogMs = now;
        const video = entry.video;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime.toFixed(2) : 'n/a';
        console.info(
            `[videoScreen] frame upload: id=${entry.id} mode=${mode} count=${entry.uploadedFrameCount + 1}` +
            ` resized=${resized} canvas=${entry.canvas.width}x${entry.canvas.height}` +
            ` video=${video.videoWidth}x${video.videoHeight} readyState=${video.readyState}` +
            ` currentTime=${currentTime} sample=${this._frameUploadSample(entry, mode)}` +
            ` gpuError=${gpuError}`
        );
    }

    private _logVideoCanvasProbe(entry: VideoEntry, probe: VideoCanvasProbe): void {
        const now = performance.now();
        if (now - entry.lastVideoProbeLogMs < 5000 && !(probe.usable && probe.nonBlack)) return;
        entry.lastVideoProbeLogMs = now;
        const video = entry.video;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime.toFixed(2) : 'n/a';
        console.info(
            `[videoScreen] video canvas probe: id=${entry.id}` +
            ` usable=${probe.usable} nonBlack=${probe.nonBlack}` +
            ` bright=${probe.brightPixels}/${probe.totalPixels}` +
            ` video=${video.videoWidth}x${video.videoHeight} readyState=${video.readyState}` +
            ` currentTime=${currentTime} sample=${probe.sample}`
        );
    }

    private _logVideoFrameCallback(entry: VideoEntry, metadata: VideoFrameCallbackMetadata): void {
        const now = performance.now();
        if (now - entry.lastVideoFrameCallbackLogMs < 5000) return;
        entry.lastVideoFrameCallbackLogMs = now;
        const video = entry.video;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime.toFixed(2) : 'n/a';
        const mediaTime = Number.isFinite(metadata.mediaTime) ? metadata.mediaTime!.toFixed(2) : 'n/a';
        const presentedFrames = Number.isFinite(metadata.presentedFrames) ? metadata.presentedFrames : 'n/a';
        console.info(
            `[videoScreen] video frame callback: id=${entry.id}` +
            ` video=${video.videoWidth}x${video.videoHeight} readyState=${video.readyState}` +
            ` currentTime=${currentTime} mediaTime=${mediaTime}` +
            ` presentedFrames=${presentedFrames}`
        );
    }

    private _logVideoUploadStage(entry: VideoEntry, stage: string, detail: string): void {
        const now = performance.now();
        if (stage === 'delayed') {
            if (now - entry.lastVideoStageLogMs < 1000) return;
            entry.lastVideoStageLogMs = now;
        } else {
            if (stage !== 'delayed' && now - entry.lastVideoDirectStageLogMs < 5000) return;
            entry.lastVideoDirectStageLogMs = now;
        }
        const video = entry.video;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime.toFixed(2) : 'n/a';
        console.info(
            `[videoScreen] upload stage: id=${entry.id} stage=${stage}` +
            ` sourceMode=${entry.sourceMode} readyState=${video.readyState}` +
            ` networkState=${video.networkState} video=${video.videoWidth}x${video.videoHeight}` +
            ` currentTime=${currentTime} detail=${detail}`
        );
    }

    private _frameUploadSample(entry: VideoEntry, mode: string): string {
        if (mode === 'video') return 'direct-video';
        return this._sampleCanvasPixels(entry);
    }

    private _sampleCanvasPixels(entry: VideoEntry): string {
        if (!entry.ctx || entry.canvas.width <= 0 || entry.canvas.height <= 0) return 'n/a';
        try {
            const points = [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]];
            return points.map(([px, py]) => {
                const x = Math.min(entry.canvas.width - 1, Math.max(0, Math.floor(entry.canvas.width * px)));
                const y = Math.min(entry.canvas.height - 1, Math.max(0, Math.floor(entry.canvas.height * py)));
                const data = entry.ctx!.getImageData(x, y, 1, 1).data;
                return `${data[0]},${data[1]},${data[2]},${data[3]}`;
            }).join('|');
        } catch (err) {
            return `error:${err instanceof Error ? err.name : String(err)}`;
        }
    }

    private _graphicsDeviceError(): string {
        const gl = (this._app.graphicsDevice as unknown as {
            gl?: WebGLRenderingContext | WebGL2RenderingContext;
        }).gl;
        if (!gl?.getError) return 'n/a';
        const err = gl.getError();
        return err === gl.NO_ERROR ? '0' : `0x${err.toString(16)}`;
    }

    private _isGpuUploadError(gpuError: string): boolean {
        return gpuError !== '0' && gpuError !== 'n/a';
    }

    private _logVideoState(entry: VideoEntry, event: string): void {
        const video = entry.video;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime.toFixed(2) : 'n/a';
        const error = video.error ? ` error=${video.error.code}:${video.error.message}` : '';
        console.info(
            `[videoScreen] ${event}: id=${entry.id} mobile=${entry.mobileVideo} sourceMode=${entry.sourceMode}` +
            ` readyState=${video.readyState} networkState=${video.networkState} paused=${video.paused}` +
            ` muted=${video.muted} videoWidth=${video.videoWidth} videoHeight=${video.videoHeight}` +
            ` currentTime=${currentTime} currentSrc=${video.currentSrc || entry.config.url}${error}`
        );
    }

    private _shouldUploadFrame(entry: VideoEntry, now: number): boolean {
        if (!entry.sourceAttached) return true;
        if (entry.sourceMode === 'canvas' && this._frameCanvasNeedsResize(entry)) return true;
        if (now - entry.lastUploadMs < entry.uploadMinIntervalMs) return false;
        return entry.video.currentTime !== entry.lastUploadedVideoTime;
    }

    private _resizeFrameCanvas(entry: VideoEntry): boolean {
        const size = this._frameCanvasSize(entry);
        if (entry.canvas.width === size.width && entry.canvas.height === size.height) return false;
        entry.canvas.width = size.width;
        entry.canvas.height = size.height;
        if (entry.ctx) {
            entry.ctx.imageSmoothingEnabled = true;
            entry.ctx.imageSmoothingQuality = 'high';
        }
        return true;
    }

    private _frameCanvasNeedsResize(entry: VideoEntry): boolean {
        const size = this._frameCanvasSize(entry);
        return entry.canvas.width !== size.width || entry.canvas.height !== size.height;
    }

    private _frameCanvasSize(entry: VideoEntry): { width: number; height: number } {
        const sourceWidth = entry.video.videoWidth || entry.canvas.width || 4;
        const sourceHeight = entry.video.videoHeight || entry.canvas.height || 4;
        const scale = Math.min(
            1,
            entry.maxTextureWidth / sourceWidth,
            entry.maxTextureHeight / sourceHeight
        );
        return {
            width: Math.max(2, Math.round(sourceWidth * scale)),
            height: Math.max(2, Math.round(sourceHeight * scale))
        };
    }
}
